import { useEffect } from "react";
import BatonRougeHeroSection from "./components/HeroSection";
import BatonRougeStickyNav from "./components/StickyNav";
import BatonRougeProcessingTimeSection from "./components/ProcessingTimeSection";
import BatonRougeServicesSection from "./components/ServicesSection";
import BatonRougeLocalAreaSection from "./components/LocalAreaSection";
import BatonRougeTravelTimingSection from "./components/TravelTimingSection";
import BatonRougeDestinationsSection from "./components/DestinationsSection";
import BatonRougeTestimonialsSection from "./components/TestimonialsSection";
import BatonRougeAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import BatonRougeComparisonSection from "./components/ComparisonSection";
import BatonRougeMistakesSection from "./components/MistakesSection";
import BatonRougeHowItWorksSection from "./components/HowItWorksSection";
import BatonRougeFAQSection, { faqs } from "./components/FAQSection";
import BatonRougeHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import BatonRougeFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function BatonRougePage() {
  useEffect(() => {
    document.title = "Emergency Passport Baton Rouge, LA — Expedited Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/baton-rouge/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Baton Rouge, LA — as fast as 24 hours. LSU students, petrochemical workers, and state employees served. A+ BBB rated, registered State Dept. courier serving Baton Rouge, Zachary, Port Allen, and the Capital Region.");
    setMeta("geo.placename", "Baton Rouge, Louisiana");
    setMeta("geo.region", "US-LA");
    setMeta("geo.position", "30.4515;-91.1871");
    setMeta("ICBM", "30.4515, -91.1871");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens." },
      { name: "New Passport", description: "First-time passport application assistance." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service." },
      { name: "Child Passport", description: "Passport services for minors under 16." },
      { name: "Name Change", description: "Passport correction and name change service." },
      { name: "Second Passport & Damaged", description: "Second passport issuance and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Louisiana", item: "https://fastpassportcenter.com/louisiana/" },
          { "@type": "ListItem", position: 3, name: "Baton Rouge Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Emergency and expedited passport services for Baton Rouge and the Capital Region of Louisiana.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: { "@type": "City", name: "Baton Rouge", containedInPlace: { "@type": "State", name: "Louisiana" } },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "1200" },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: { "@type": "LocalBusiness", name: "Fast Passport Center" },
        areaServed: { "@type": "City", name: "Baton Rouge" },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      document.getElementById("structured-data")?.remove();
      document.querySelector('link[rel="canonical"]')?.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <BatonRougeHeroSection />
      <BatonRougeStickyNav />
      <BatonRougeProcessingTimeSection />
      <BatonRougeServicesSection />
      <BatonRougeLocalAreaSection />
      <BatonRougeTravelTimingSection />
      <BatonRougeDestinationsSection />
      <BatonRougeTestimonialsSection />
      <BatonRougeAcceptanceFacilitiesSection />
      <BatonRougeComparisonSection />
      <BatonRougeMistakesSection />
      <BatonRougeHowItWorksSection />
      <BatonRougeFAQSection />
      <BatonRougeHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/baton-rouge" state="LA" />
      <BatonRougeFinalCTA />
    </div>
  );
}